import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { syllabusApi } from "../config/syllabusApi";
import Toast from "../Toast/Toast";
import "./CurrentAffairsQuizAdmin.css";

const formatValue = (value) => (value == null || value === "" ? "NA" : String(value));

const getErrorMessage = (error, fallback) => {
  if (Number(error?.status) === 403) return "Not authorized.";
  if (String(error?.message || "").trim().toLowerCase() === "failed to fetch") {
    return "Unable to reach the admin API right now. Check the backend/server connection and try again.";
  }
  return error?.message || fallback;
};

const readExamKey = (item) =>
  String(item?.examKey || item?.examCode || item?.exam_code || item?.key || "").trim();

const readExamName = (item) =>
  String(item?.examName || item?.exam_name || item?.title || "").trim();

const readCoverageSubjects = (payload) => {
  if (Array.isArray(payload?.subjects)) return payload.subjects;
  if (Array.isArray(payload?.data?.subjects)) return payload.data.subjects;
  if (Array.isArray(payload?.coverage?.subjects)) return payload.coverage.subjects;
  return [];
};

const readPreviewQuestions = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.questions)) return payload.questions;
  if (Array.isArray(payload?.data?.questions)) return payload.data.questions;
  return [];
};

const readPreviewMeta = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      generatedCount: 0,
      requestedTotal: 0,
      skippedExistingDuplicates: 0,
      skippedGeneratedDuplicates: 0,
      complete: false,
      message: "",
    };
  }

  return {
    generatedCount: payload?.generatedCount ?? payload?.meta?.generatedCount ?? 0,
    requestedTotal: payload?.requestedTotal ?? payload?.meta?.requestedTotal ?? 0,
    skippedExistingDuplicates:
      payload?.skippedExistingDuplicates ?? payload?.meta?.skippedExistingDuplicates ?? 0,
    skippedGeneratedDuplicates:
      payload?.skippedGeneratedDuplicates ?? payload?.meta?.skippedGeneratedDuplicates ?? 0,
    complete: Boolean(payload?.complete ?? payload?.meta?.complete),
    message: String(payload?.message ?? payload?.meta?.message ?? "").trim(),
  };
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const readCorrectAnswerValue = (question) => {
  const firstChoiceId = question?.correctAnswer?.choiceIds?.[0];
  if (firstChoiceId == null || firstChoiceId === "") return "NA";
  return String(firstChoiceId);
};

const sanitizeInlineSvg = (svgMarkup) => {
  const raw = String(svgMarkup || "").trim();
  if (!raw.toLowerCase().startsWith("<svg")) return "";
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") return "";

  try {
    const parser = new window.DOMParser();
    const documentNode = parser.parseFromString(raw, "image/svg+xml");
    if (documentNode.querySelector("parsererror")) return "";

    const allowedTags = new Set([
      "svg",
      "g",
      "path",
      "circle",
      "ellipse",
      "rect",
      "line",
      "polyline",
      "polygon",
      "text",
      "tspan",
      "defs",
      "lineargradient",
      "radialgradient",
      "stop",
      "clippath",
      "mask",
      "pattern",
      "marker",
      "symbol",
      "use",
      "title",
      "desc",
    ]);

    const allowedAttributes = new Set([
      "id",
      "class",
      "viewbox",
      "width",
      "height",
      "x",
      "y",
      "x1",
      "x2",
      "y1",
      "y2",
      "cx",
      "cy",
      "r",
      "rx",
      "ry",
      "d",
      "points",
      "fill",
      "fill-opacity",
      "fill-rule",
      "stroke",
      "stroke-width",
      "stroke-linecap",
      "stroke-linejoin",
      "stroke-opacity",
      "stroke-dasharray",
      "stroke-dashoffset",
      "opacity",
      "transform",
      "preserveaspectratio",
      "xmlns",
      "xmlns:xlink",
      "href",
      "xlink:href",
      "gradientunits",
      "gradienttransform",
      "offset",
      "stop-color",
      "stop-opacity",
      "clip-path",
      "mask",
      "marker-start",
      "marker-mid",
      "marker-end",
      "font-size",
      "font-family",
      "font-weight",
      "text-anchor",
      "dominant-baseline",
    ]);

    Array.from(documentNode.querySelectorAll("*")).forEach((element) => {
      const tagName = element.tagName.toLowerCase();
      if (!allowedTags.has(tagName)) {
        element.remove();
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = String(attribute.value || "").trim();
        const isEventHandler = name.startsWith("on");
        const isUnsafeHref =
          (name === "href" || name === "xlink:href") &&
          value &&
          !value.startsWith("#") &&
          !value.startsWith("data:image/");

        if (isEventHandler || !allowedAttributes.has(name) || isUnsafeHref) {
          element.removeAttribute(attribute.name);
        }
      });
    });

    return documentNode.documentElement?.tagName?.toLowerCase() === "svg"
      ? documentNode.documentElement.outerHTML
      : "";
  } catch {
    return "";
  }
};

function InlineSvgPreview({ svgMarkup, title }) {
  const sanitizedMarkup = useMemo(() => sanitizeInlineSvg(svgMarkup), [svgMarkup]);

  if (!sanitizedMarkup) return null;

  return (
    <div
      className="ca-admin-inline-svg"
      role="img"
      aria-label={title || "SVG preview"}
      dangerouslySetInnerHTML={{ __html: sanitizedMarkup }}
    />
  );
}

function QuestionPreviewList({ previewQuestions }) {
  return (
    <div className="ca-admin-question-preview-list">
      {previewQuestions.map((question, index) => {
        const choices = Array.isArray(question?.choices) ? question.choices : [];

        return (
          <article
            className="ca-admin-question-preview-card"
            key={`${question?.questionCode || question?.id || "preview-question"}-${index}`}
          >
            <div className="ca-admin-question-preview-header">
              <strong>Q{index + 1}</strong>
              <span>{question?.questionCode || "No code"}</span>
            </div>

            <p className="ca-admin-question-preview-text">
              {question?.questionText || "Question text unavailable."}
            </p>

            <InlineSvgPreview
              svgMarkup={question?.questionImage}
              title={`Question ${index + 1} image`}
            />

            <div className="ca-admin-question-preview-choices">
              {choices.map((choice, choiceIndex) => (
                <div
                  className="ca-admin-question-choice"
                  key={`${choice?.id || choice?.choiceId || "choice"}-${choiceIndex}`}
                >
                  <strong>{choiceIndex + 1}.</strong>
                  <div>
                    <div>{choice?.text || "No choice text"}</div>
                    <InlineSvgPreview
                      svgMarkup={choice?.image}
                      title={`Question ${index + 1} choice ${choiceIndex + 1} image`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <p className="ca-admin-question-preview-answer">
              Correct answer index: <strong>{readCorrectAnswerValue(question)}</strong>
            </p>
          </article>
        );
      })}
    </div>
  );
}

function AdminSyllabusQuestionBankPage() {
  const navigate = useNavigate();
  const [examRows, setExamRows] = useState([]);
  const [selectedExamKey, setSelectedExamKey] = useState("");
  const [selectedSubjectName, setSelectedSubjectName] = useState("");
  const [coverageSubjects, setCoverageSubjects] = useState([]);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [examsLoading, setExamsLoading] = useState(true);
  const [activeTopicKey, setActiveTopicKey] = useState("");
  const [savingPreview, setSavingPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("raw");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [requestForm, setRequestForm] = useState({
    totalQuestions: 100,
    easyCount: 20,
    mediumCount: 40,
    hardCount: 40,
    marks: 1,
    negativeMarks: 0.25,
    isPremium: false,
    createdBy: "ADMIN_UI",
  });

  const selectedExam = useMemo(
    () => examRows.find((row) => readExamKey(row) === selectedExamKey) || null,
    [examRows, selectedExamKey]
  );

  const selectedSubject = useMemo(
    () =>
      coverageSubjects.find(
        (subject) => String(subject?.subjectName || "").trim() === selectedSubjectName
      ) || null,
    [coverageSubjects, selectedSubjectName]
  );

  const subjectTopics = useMemo(
    () => (Array.isArray(selectedSubject?.topics) ? selectedSubject.topics : []),
    [selectedSubject]
  );

  const previewMeta = useMemo(() => readPreviewMeta(previewPayload), [previewPayload]);
  const previewQuestions = useMemo(() => readPreviewQuestions(previewPayload), [previewPayload]);
  const rawPreviewText = useMemo(
    () => (previewPayload ? JSON.stringify(previewPayload, null, 2) : ""),
    [previewPayload]
  );

  const loadExams = async () => {
    setExamsLoading(true);
    setError("");

    try {
      const data = await syllabusApi.adminList();
      const nextRows = Array.isArray(data) ? data : [];
      setExamRows(nextRows);
      setSelectedExamKey((current) => current || readExamKey(nextRows[0]) || "");
    } catch (err) {
      setExamRows([]);
      setError(getErrorMessage(err, "Unable to load exams."));
    } finally {
      setExamsLoading(false);
    }
  };

  useEffect(() => {
    loadExams();
  }, []);

  useEffect(() => {
    if (!selectedExamKey) {
      setCoverageSubjects([]);
      setSelectedSubjectName("");
      setPreviewPayload(null);
      setIsPreviewModalOpen(false);
      return;
    }

    let mounted = true;

    const loadCoverage = async () => {
      setCoverageLoading(true);
      setError("");
      setMessage("");
      setPreviewPayload(null);
      setIsPreviewModalOpen(false);

      try {
        const data = await syllabusApi.adminQuestionBankCoverage(selectedExamKey);
        if (!mounted) return;
        const nextSubjects = readCoverageSubjects(data);
        setCoverageSubjects(nextSubjects);
        setSelectedSubjectName((current) => {
          const hasCurrent = nextSubjects.some(
            (subject) => String(subject?.subjectName || "").trim() === current
          );
          return hasCurrent ? current : String(nextSubjects[0]?.subjectName || "").trim();
        });
      } catch (err) {
        if (!mounted) return;
        setCoverageSubjects([]);
        setSelectedSubjectName("");
        setError(getErrorMessage(err, "Unable to load coverage."));
      } finally {
        if (mounted) setCoverageLoading(false);
      }
    };

    loadCoverage();

    return () => {
      mounted = false;
    };
  }, [selectedExamKey]);

  const handleRequestFormChange = (field, value) => {
    setRequestForm((current) => ({ ...current, [field]: value }));
  };

  const handleGeneratePreview = async (topicName) => {
    const subjectName = String(selectedSubject?.subjectName || "").trim();
    const totalQuestions = toNumber(requestForm.totalQuestions);
    const easyCount = toNumber(requestForm.easyCount);
    const mediumCount = toNumber(requestForm.mediumCount);
    const hardCount = toNumber(requestForm.hardCount);

    if (!subjectName) {
      const nextError = "Select a subject first.";
      setError(nextError);
      setToastMessage(nextError);
      return;
    }

    if (easyCount + mediumCount + hardCount !== totalQuestions) {
      const nextError = "Easy, medium, and hard counts must add up to total questions.";
      setError(nextError);
      setToastMessage(nextError);
      return;
    }

    const topicKey = `${subjectName}::${topicName}`;
    setActiveTopicKey(topicKey);
    setPreviewLoading(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        subject: subjectName,
        topic: topicName,
        totalQuestions,
        easyCount,
        mediumCount,
        hardCount,
        language: "HINDI",
        marks: toNumber(requestForm.marks, 1),
        negativeMarks: toNumber(requestForm.negativeMarks, 0.25),
        isPremium: Boolean(requestForm.isPremium),
        createdBy: String(requestForm.createdBy || "ADMIN_UI").trim() || "ADMIN_UI",
      };

      const result = await syllabusApi.adminGenerateQuestionBankPreview(selectedExamKey, payload);
      setPreviewPayload(result);
      setPreviewMode("raw");
      setIsPreviewModalOpen(true);
      setMessage(`Preview generated for ${subjectName} / ${topicName}.`);
      setToastMessage("Preview generated.");
    } catch (err) {
      const nextError = getErrorMessage(err, "Unable to generate question preview.");
      setError(nextError);
      setToastMessage(nextError);
      setPreviewPayload(null);
      setIsPreviewModalOpen(false);
    } finally {
      setActiveTopicKey("");
      setPreviewLoading(false);
    }
  };

  const handleSavePreview = async () => {
    if (!previewQuestions.length) {
      const nextError = "Generate a preview before saving.";
      setError(nextError);
      setToastMessage(nextError);
      return;
    }

    setSavingPreview(true);
    setError("");

    try {
      const result = await syllabusApi.adminSaveQuestionBankPreview({ questions: previewQuestions });
      const nextMessage = result?.message || "Preview saved to the question bank.";
      setMessage(nextMessage);
      setToastMessage(nextMessage);
      setIsPreviewModalOpen(false);
    } catch (err) {
      const nextError = getErrorMessage(err, "Unable to save question preview.");
      setError(nextError);
      setToastMessage(nextError);
    } finally {
      setSavingPreview(false);
    }
  };

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header">
        <h1>Question Bank Flow</h1>
        <div className="ca-admin-actions">
          <button type="button" className="ca-admin-btn" onClick={() => navigate("/admin/syllabus")}>
            Back To Syllabus
          </button>
          <button type="button" className="ca-admin-btn" onClick={loadExams} disabled={examsLoading}>
            Refresh Exams
          </button>
        </div>
      </div>

      {message ? <p className="ca-admin-msg ok">{message}</p> : null}
      {error ? <p className="ca-admin-msg err">{error}</p> : null}

      <section className="ca-admin-list-card ca-admin-form-card ca-admin-question-bank-top-card">
        <div className="ca-admin-question-bank-top-grid">
          <div className="ca-admin-filter-group">
            <label htmlFor="question-bank-exam-select">Exam</label>
            <select
              id="question-bank-exam-select"
              value={selectedExamKey}
              onChange={(event) => setSelectedExamKey(event.target.value)}
              disabled={examsLoading || !examRows.length}
            >
              <option value="">Select exam</option>
              {examRows.map((row, index) => {
                const examKey = readExamKey(row);
                return (
                  <option key={`${examKey || "exam"}-${index}`} value={examKey}>
                    {readExamName(row) || examKey || "Untitled Exam"}
                    {examKey ? ` (${examKey})` : ""}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="ca-admin-filter-group">
            <label htmlFor="question-bank-subject-select">Subject</label>
            <select
              id="question-bank-subject-select"
              value={selectedSubjectName}
              onChange={(event) => setSelectedSubjectName(event.target.value)}
              disabled={coverageLoading || !coverageSubjects.length}
            >
              <option value="">Select subject</option>
              {coverageSubjects.map((subject, index) => {
                const subjectName = String(subject?.subjectName || "").trim();
                return (
                  <option key={`${subjectName || "subject"}-${index}`} value={subjectName}>
                    {subjectName || "Untitled Subject"}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div
          className="ca-admin-stats-grid ca-admin-stats-grid-compact ca-admin-question-bank-summary-grid"
          style={{ marginTop: 16 }}
        >
          <div className="ca-admin-stat-card">
            <span>Exam Name</span>
            <strong>{readExamName(selectedExam) || "NA"}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Exam Key</span>
            <strong>{readExamKey(selectedExam) || "NA"}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Selected Subject</span>
            <strong>{selectedSubjectName || "NA"}</strong>
          </div>
          <div className="ca-admin-stat-card">
            <span>Topics In Subject</span>
            <strong>{subjectTopics.length}</strong>
          </div>
        </div>

        <div className="ca-admin-question-bank-form-grid ca-admin-question-bank-form-grid-top">
          <div>
            <label htmlFor="question-bank-total">Total Questions</label>
            <input
              id="question-bank-total"
              type="number"
              min="1"
              value={requestForm.totalQuestions}
              onChange={(event) => handleRequestFormChange("totalQuestions", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="question-bank-easy">Easy Count</label>
            <input
              id="question-bank-easy"
              type="number"
              min="0"
              value={requestForm.easyCount}
              onChange={(event) => handleRequestFormChange("easyCount", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="question-bank-medium">Medium Count</label>
            <input
              id="question-bank-medium"
              type="number"
              min="0"
              value={requestForm.mediumCount}
              onChange={(event) => handleRequestFormChange("mediumCount", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="question-bank-hard">Hard Count</label>
            <input
              id="question-bank-hard"
              type="number"
              min="0"
              value={requestForm.hardCount}
              onChange={(event) => handleRequestFormChange("hardCount", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="question-bank-marks">Marks</label>
            <input
              id="question-bank-marks"
              type="number"
              min="0"
              step="0.01"
              value={requestForm.marks}
              onChange={(event) => handleRequestFormChange("marks", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="question-bank-negative">Negative Marks</label>
            <input
              id="question-bank-negative"
              type="number"
              min="0"
              step="0.01"
              value={requestForm.negativeMarks}
              onChange={(event) => handleRequestFormChange("negativeMarks", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="question-bank-language">Language</label>
            <input id="question-bank-language" value="HINDI" disabled readOnly />
          </div>
          <div>
            <label htmlFor="question-bank-created-by">Created By</label>
            <input
              id="question-bank-created-by"
              value={requestForm.createdBy}
              onChange={(event) => handleRequestFormChange("createdBy", event.target.value)}
            />
          </div>
        </div>

        <label className="ca-admin-checkbox">
          <input
            type="checkbox"
            checked={requestForm.isPremium}
            onChange={(event) => handleRequestFormChange("isPremium", event.target.checked)}
          />
          <span>Premium Question Set</span>
        </label>
      </section>

      <section className="ca-admin-list-card" style={{ marginTop: 16 }}>
        <div className="ca-admin-header">
          <h2>Subject Details</h2>
          {coverageLoading ? <span className="ca-admin-status-badge loading">Loading</span> : null}
        </div>

        {!coverageLoading && !selectedExamKey ? <p>Select an exam to load details.</p> : null}
        {!coverageLoading && selectedExamKey && !selectedSubjectName ? (
          <p>Select a subject to view topic details.</p>
        ) : null}
        {!coverageLoading && selectedSubjectName && !subjectTopics.length ? (
          <p>No topics found for this subject.</p>
        ) : null}

        {selectedSubject ? (
          <>
            <div className="ca-admin-table-wrap" style={{ marginTop: 16 }}>
              <table className="ca-admin-table">
                <thead>
                  <tr>
                    <th>Topic</th>
                    <th>Available In Exam</th>
                    <th>Available Overall</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectTopics.map((topic, topicIndex) => {
                    const topicName = String(topic?.topicName || "").trim() || "Untitled Topic";
                    const topicKey = `${selectedSubjectName}::${topicName}`;

                    return (
                      <tr key={`${topicKey}-${topicIndex}`}>
                        <td data-label="Topic">{topicName}</td>
                        <td data-label="Available In Exam">
                          {formatValue(topic?.availableQuestionsInExam)}
                        </td>
                        <td data-label="Available Overall">
                          {formatValue(topic?.availableQuestionsOverall)}
                        </td>
                        <td data-label="Actions">
                          <button
                            type="button"
                            className="ca-admin-btn ca-admin-btn-primary"
                            disabled={previewLoading || activeTopicKey === topicKey}
                            onClick={() => handleGeneratePreview(topicName)}
                          >
                            {activeTopicKey === topicKey ? "Generating..." : "Generate Questions"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>

      {isPreviewModalOpen ? (
        <div
          className="ca-admin-modal-backdrop"
          role="presentation"
          onClick={() => setIsPreviewModalOpen(false)}
        >
          <div
            className="ca-admin-modal-shell ca-admin-modal-shell-wide ca-admin-question-panel-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Generated questions preview"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ca-admin-header">
              <h2>Generated Questions</h2>
              <div className="ca-admin-actions">
                <button
                  type="button"
                  className={`ca-admin-subnav-btn ${previewMode === "raw" ? "active" : ""}`}
                  onClick={() => setPreviewMode("raw")}
                >
                  Raw Response
                </button>
                <button
                  type="button"
                  className={`ca-admin-subnav-btn ${previewMode === "preview" ? "active" : ""}`}
                  onClick={() => setPreviewMode("preview")}
                >
                  Preview Questions
                </button>
                <button
                  type="button"
                  className="ca-admin-btn"
                  onClick={() => setIsPreviewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="ca-admin-stats-grid ca-admin-stats-grid-compact">
              <div className="ca-admin-stat-card">
                <span>Generated</span>
                <strong>{formatValue(previewMeta.generatedCount)}</strong>
              </div>
              <div className="ca-admin-stat-card">
                <span>Requested</span>
                <strong>{formatValue(previewMeta.requestedTotal)}</strong>
              </div>
              <div className="ca-admin-stat-card">
                <span>Skipped Existing</span>
                <strong>{formatValue(previewMeta.skippedExistingDuplicates)}</strong>
              </div>
              <div className="ca-admin-stat-card">
                <span>Skipped Generated</span>
                <strong>{formatValue(previewMeta.skippedGeneratedDuplicates)}</strong>
              </div>
            </div>

            {previewMeta.message ? (
              <p className="ca-admin-tool-copy" style={{ marginTop: 12 }}>
                {previewMeta.message}
              </p>
            ) : null}

            {previewMode === "raw" ? (
              <div className="ca-admin-form-card" style={{ marginTop: 16 }}>
                <label htmlFor="question-bank-preview-json">Generated Response</label>
                <textarea
                  id="question-bank-preview-json"
                  rows={22}
                  readOnly
                  spellCheck={false}
                  value={rawPreviewText}
                />
              </div>
            ) : (
              <div className="ca-admin-modal-preview-shell">
                {previewQuestions.length ? (
                  <QuestionPreviewList previewQuestions={previewQuestions} />
                ) : (
                  <p>No preview questions returned.</p>
                )}
              </div>
            )}

            <div className="ca-admin-actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="ca-admin-btn ca-admin-btn-primary"
                onClick={handleSavePreview}
                disabled={savingPreview || !previewQuestions.length}
              >
                {savingPreview ? "Saving..." : "Save Preview To DB"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
}

export default AdminSyllabusQuestionBankPage;
